/**
 * Dieux standard-events bridge — the single owner of three theme-wide Shopify standard-events behaviors:
 */

const loadModule = globalThis.importShim ?? ((specifier) => import(specifier));

/** @type {Promise<object | null>} Shopify's standard-events module, or null when it cannot load. */
export const standardEvents = loadModule('@theme/standard-events').catch(() => null);

/** @type {object | null} The loaded standard-events module, for callers that cannot wait. */
export let loadedStandardEvents = null;

standardEvents.then((module) => {
  loadedStandardEvents = module;
});

if (typeof document !== 'undefined' && !globalThis.__dieuxStandardEventsBridge) {
  globalThis.__dieuxStandardEventsBridge = true;

  standardEvents.then((standardEvents) => {
    if (
      standardEvents &&
      typeof standardEvents.createViewEventElement === 'function' &&
      !customElements.get('s-view-event')
    ) {
      customElements.define('s-view-event', standardEvents.createViewEventElement());
    }
  });

  const domReady = document.readyState === 'loading'
    ? new Promise((resolve) => {
      document.addEventListener('DOMContentLoaded', resolve, { once: true });
    })
    : Promise.resolve();

  // A prerendered document must not report page or product views until the
  // visit actually happens; activation is the earliest truthful moment.
  const activated = document.prerendering
    ? new Promise((resolve) => {
      document.addEventListener('prerenderingchange', resolve, { once: true });
    })
    : Promise.resolve();

  Promise.all([standardEvents, domReady, activated]).then(([standardEvents]) => {
    if (!standardEvents || typeof standardEvents.PageViewEvent !== 'function') return;
    const main = document.getElementById('MainContent');
    try {
      document.dispatchEvent(new standardEvents.PageViewEvent({
        page: {
          template: main?.dataset.template || '',
          title: document.title,
          url: window.location.href,
        },
      }));
    } catch {
      // Interop must never break page load.
    }
  });

  const VIEW_SELECTOR = '[view-event-payload]:not(s-view-event):not([view-event-trigger])';
  const dispatched = new WeakSet();

  Promise.all([standardEvents, domReady, activated]).then(([standardEvents]) => {
    if (!standardEvents) return;

    const eventFor = (payload, element) => {
      const context = payload.context
        || (element.closest('dialog') ? 'dialog' : 'page');
      if (payload.product && typeof standardEvents.ProductViewEvent === 'function') {
        return new standardEvents.ProductViewEvent({ ...payload, context });
      }
      if (payload.cart && typeof standardEvents.CartViewEvent === 'function') {
        return new standardEvents.CartViewEvent({ ...payload, context });
      }
      return null;
    };

    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        const element = entry.target;
        observer.unobserve(element);
        if (dispatched.has(element)) continue;
        dispatched.add(element);
        try {
          const payload = JSON.parse(element.getAttribute('view-event-payload'));
          const event = payload && typeof payload === 'object' ? eventFor(payload, element) : null;
          if (event) element.dispatchEvent(event);
        } catch {
          // A malformed payload must never break the owning component.
        }
      }
    });

    const observe = (element) => {
      if (!dispatched.has(element)) observer.observe(element);
    };

    const scan = (root) => {
      if (!(root instanceof Element)) return;
      if (root.matches(VIEW_SELECTOR)) observe(root);
      root.querySelectorAll(VIEW_SELECTOR).forEach(observe);
    };

    scan(document.body);

    const mutationObserver = new MutationObserver((records) => {
      for (const record of records) {
        record.addedNodes.forEach(scan);
      }
    });
    mutationObserver.observe(document.body, { childList: true, subtree: true });
  });
}
